import React, { useState, useEffect, useRef } from "react";
import "./About.css";

// Import doctor images
import doctorImage from "../assets/doctor-image.jpg";
import doctorDeepaImage from "../assets/Doctorimg.jpeg";

// Import Google Drive API
import { GoogleDriveAPI } from "../google-drive-api";

// Your Google Drive folder ID
const GOOGLE_DRIVE_FOLDER_ID = '1DoNiZMzbcNmNVJYNatti4KcOBr9FxKZq';

// Fallback images (will show if Google Drive fails)
const fallbackImages = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
    thumbnail: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    alt: "Counselling Workshop",
    displayName: "Counselling Workshop",
    fileName: "workshop_1.jpg"
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
    thumbnail: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    alt: "Therapy Session",
    displayName: "Therapy Session",
    fileName: "therapy_1.jpg"
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
    thumbnail: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    alt: "Group Therapy",
    displayName: "Group Therapy",
    fileName: "group_therapy_1.jpg"
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
    thumbnail: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    alt: "Mental Health Seminar",
    displayName: "Mental Health Seminar",
    fileName: "seminar_1.jpg"
  }
];

const About = () => {
  const [galleryImages, setGalleryImages] = useState(fallbackImages); // Start with fallback
  const [galleryLoading, setGalleryLoading] = useState(false); // Start as not loading
  const [galleryError, setGalleryError] = useState(null);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [usingGoogleDrive, setUsingGoogleDrive] = useState(false);
  const galleryTrackRef = useRef(null);

  // Fetch images on component mount
  useEffect(() => {
    const loadImages = async () => {
      try {
        setGalleryLoading(true);
        
        console.log('🔄 Attempting to fetch images from Google Drive...');
        
        // Initialize Google Drive API
        const driveAPI = new GoogleDriveAPI(GOOGLE_DRIVE_FOLDER_ID);
        
        // Try to fetch from Google Drive
        const driveImages = await driveAPI.fetchImages();
        
        console.log('📊 Google Drive API returned:', driveImages?.length || 0, 'images');
        
        if (driveImages && driveImages.length > 0) {
          // Test if images actually load
          const testedImages = await testImageUrls(driveImages);
          
          if (testedImages.length > 0) {
            setGalleryImages(testedImages);
            setUsingGoogleDrive(true);
            setGalleryError(null);
            console.log(`✅ Successfully loaded ${testedImages.length} images from Google Drive`);
          } else {
            // Images didn't load, use fallback
            setGalleryImages(fallbackImages);
            setUsingGoogleDrive(false);
            setGalleryError('Images found but cannot be displayed. Make sure folder is shared publicly.');
            console.log('⚠️ Google Drive images found but cannot be loaded');
          }
        } else {
          // Use fallback images
          setGalleryImages(fallbackImages);
          setUsingGoogleDrive(false);
          setGalleryError('No images found in Google Drive. Showing sample gallery.');
          console.log('ℹ Using fallback images');
        }
        
      } catch (error) {
        console.error('❌ Error loading images:', error);
        setGalleryImages(fallbackImages);
        setUsingGoogleDrive(false);
        setGalleryError('Error connecting to Google Drive. Showing sample images.');
      } finally {
        setGalleryLoading(false);
      }
    };

    // Load images with a small delay
    setTimeout(() => {
      loadImages();
    }, 1000);
  }, []);

  // Function to test if image URLs actually load
  const testImageUrls = async (images) => {
    const testedImages = [];
    
    for (const image of images) {
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            console.log(`✅ Image loaded: ${image.displayName}`);
            testedImages.push(image);
            resolve();
          };
          img.onerror = () => {
            console.log(`❌ Failed to load: ${image.displayName}`);
            // Try alternative URL format
            const alternativeImage = {
              ...image,
              src: `https://lh3.googleusercontent.com/d/${image.id}=w800-h600`,
              thumbnail: `https://lh3.googleusercontent.com/d/${image.id}=w400-h300`
            };
            testedImages.push(alternativeImage);
            resolve();
          };
          img.src = image.src;
        });
      } catch (error) {
        console.log(`⚠️ Skipping image: ${image.displayName}`);
      }
    }
    
    return testedImages;
  };

  // Gallery auto-scroll
  useEffect(() => {
    let intervalId;
    
    if (!showAllGallery && galleryImages.length > 0 && galleryTrackRef.current) {
      intervalId = setInterval(() => {
        if (galleryTrackRef.current) {
          const currentTransform = galleryTrackRef.current.style.transform;
          const currentPosition = currentTransform ? 
            parseFloat(currentTransform.replace('translateX(', '').replace('px)', '')) || 0 : 0;
          
          let newPosition = currentPosition - 1;
          const trackWidth = galleryTrackRef.current.scrollWidth / 2;
          const containerWidth = galleryTrackRef.current.parentElement?.offsetWidth || 0;
          
          if (Math.abs(newPosition) >= trackWidth - containerWidth) {
            newPosition = 0;
          }
          
          galleryTrackRef.current.style.transform = `translateX(${newPosition}px)`;
        }
      }, 30);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showAllGallery, galleryImages.length]);

  const toggleGalleryView = () => {
    setShowAllGallery(!showAllGallery);
    if (galleryTrackRef.current) {
      galleryTrackRef.current.style.transform = 'translateX(0)';
    }
  };

  // Handle image loading errors - IMPROVED VERSION
  const handleImageError = (e, image) => {
    console.error(`❌ Failed to load image: ${image.displayName}`);
    
    // Try alternative Google Drive URL formats
    if (image.id && usingGoogleDrive) {
      console.log(`🔄 Trying alternative URL for: ${image.displayName}`);
      
      // Format 1: Direct Google Drive URL
      e.target.src = `https://drive.google.com/thumbnail?id=${image.id}&sz=w800`;
      
      // If that fails, try another format
      setTimeout(() => {
        if (e.target.naturalWidth === 0) {
          // Format 2: Googleusercontent URL
          e.target.src = `https://lh3.googleusercontent.com/d/${image.id}=w800-h600`;
          
          // Last resort: Use placeholder
          setTimeout(() => {
            if (e.target.naturalWidth === 0) {
              e.target.src = `https://via.placeholder.com/800x600/0A2342/ffffff?text=${encodeURIComponent(image.displayName)}`;
            }
          }, 500);
        }
      }, 500);
    } else {
      // Fallback to placeholder
      e.target.src = `https://via.placeholder.com/800x600/0A2342/ffffff?text=${encodeURIComponent(image.displayName)}`;
    }
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content"><br></br>
            <h1>About Servite Counselling Centre</h1>
            <p>Professional Mental Health Care & Healing Services</p>
          </div>
        </div>
      </section>

      {/* Main Counsellor Section */}
      <section className="counsellor-section">
        <div className="container">
          <div className="section-header">
            <h2>Director & Lead Counsellor</h2>
            <p>Professional counselling with 15+ years of clinical experience</p>
          </div>

          <div className="profile-container">
            <div className="profile-image-original">
              <img src={doctorImage} alt="Sr. Rosari Sahaya Rani" />
            </div>
            <div className="profile-content">
              <div className="profile-header">
                <h3>Sr. Rosari Sahaya Rani MSSM </h3>
                <p className="profile-title">Licensed Counsellor, Motivational speaker, Transformational Coach And Clinical Psychotherapist</p>
              </div>

              <div className="qualifications">
                <h4>Professional Qualifications</h4>
                <ul>
                  <li>M.Sc. in Counselling and Psychotherapy</li>
                  <li>Post Graduate Diploma in Counselling & Psychotherapy</li>
                  <li>Certified Positive and Transcultural Psychotherapist by European Association for Psychotherapy from World association for positive and Transcultural psychotherapy</li>
                </ul>
              </div>

              <div className="description">
                <p>
                  Sr. Rosari Sahaya Rani is a member of the Congregation of the Mother of Sorrows, Servants of Mary. She is a highly skilled psychotherapist, counsellor, Motivational Speaker and Trainer of life transformative programmes.
                  With an impressive 25-years of service, Sr. Rosari has held roles as an esteemed educationist, psychologist, counsellor and motivational trainer in multiple states, including West Bengal, Madhya Pradesh, Maharashtra, Andhra Pradesh and Tamil Nadu. She has worked extensively with schools that follow the CBSE, ICSE and State Board curriculam.
                </p>
                <p>
                  Sr. Rosari has conducted highly impactful motivational seminars and workshops in various schools and religious formation houses, including the renowned Lumen Institute in Tindivanam and St. xavier's College, Palayamkottai.
                </p>
              </div>

              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-label">Counselling Helpline</span>
                  <span className="contact-value">+91 9301098460</span>
                </div>
                <div className="contact-item">
                  <span className="contact-label">Professional Email</span>
                  <span className="contact-value">srrosary750@gmail.com</span>
                </div>
              </div>

              <div className="expertise">
                <h4>Areas of Expertise</h4>
                <div className="expertise-tags">
                  <span>Depression Counselling</span>
                  <span>Anxiety Therapy</span>
                  <span>Relationship Issues</span>
                  <span>Family Counselling</span>
                  <span>Stress Management</span>
                  <span>Personal Growth</span>
                  <span>Career Guidance</span>
                  <span>Trauma Healing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Homoeopathy Section */}
      <section className="homoeopathy-section">
        <div className="container">
          <div className="section-header">
            <h2>Servite Homoeopathy Healthcare Centre</h2>
            <p>Natural Healing with Holistic Homoeopathic Treatment</p>
          </div>

          <div className="profile-container">
            <div className="profile-image-deepa">
              <img 
                src={doctorDeepaImage} 
                alt="Sr. Dr. Deepa - BHMS" 
              />
            </div>
            <div className="profile-content">
              <div className="profile-header">
                <h3>Sr. Dr. Deepa BHMS</h3>
                <p className="profile-title">Registered Homeopathic Practitioner</p>
              </div>

              <div className="qualifications">
                <h4>Professional Qualifications</h4>
                <ul>
                  <li>Bachelor of Homoeopathic Medicine & Surgery BHMS</li>
                  <li>Father Mullur Homoeopathic Medical College, Mangalore</li>
                  <li>Registered Homoeopathic Practitioner</li>
                </ul>
              </div>

              <div className="description">
                <p>
                  Servite Homoeopathy Healthcare Centre is a renowned healthcare providing centre, 
                  offering holistic homoeopathic treatments. Sr. Dr. Deepa focuses on individualized care, 
                  addressing physical, emotional and mental well-being.
                </p>
                <p>
                  Her dedicated services in homoeopathy help promote natural healing and wellness using 
                  safe, natural medicines that work in harmony with the body's own healing mechanisms.
                </p>
              </div>

              <div className="specializations">
                <h4>Treatment Specializations</h4>
                <div className="specialization-tags">
                  <span>Chronic Diseases</span>
                  <span>Allergies</span>
                  <span>Skin Disorders</span>
                  <span>Respiratory Issues</span>
                  <span>Digestive Problems</span>
                  <span>Women's Health</span>
                  <span>Children's Health</span>
                  <span>Lifestyle Disorders</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Gallery Section */}
      <section className="events-gallery-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Events & Workshops</h2>
            <p>Dynamic gallery of counselling sessions, mental health workshops, and therapeutic events</p>
            
            {/* Gallery Status - Always show status */}
            
          </div>
          
          <div className="gallery-container">
            {galleryLoading ? (
              <div className="gallery-loading">
                <div className="loading-spinner"></div>
                <p>Loading gallery images...</p>
                <small>Connecting to Google Drive</small>
              </div>
            ) : (
              <>
                {/* Show instructions only if not using Google Drive */}
                {!usingGoogleDrive && (
                  <div className="fallback-notice">
                    <div className="fallback-content">
                      
                    </div>
                  </div>
                )}
                
                <div className={`gallery-view ${showAllGallery ? 'grid-view' : 'slider-view'}`}>
                  {showAllGallery ? (
                    <div className="gallery-grid">
                      {galleryImages.map((image, index) => (
                        <div key={image.id || index} className="gallery-item">
                          <img 
                            src={image.src} 
                            alt={image.alt}
                            loading="lazy"
                            onError={(e) => handleImageError(e, image)}
                            style={{ minHeight: '200px', objectFit: 'cover' }}
                          />
                          <div className="image-overlay">
                            <span>{image.displayName}</span>
                            {usingGoogleDrive && <small></small>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="gallery-slider">
                      <div className="gallery-track" ref={galleryTrackRef}>
                        {/* Original images */}
                        {galleryImages.map((image, index) => (
                          <div key={image.id || index} className="gallery-slide">
                            <img 
                              src={image.src} 
                              alt={image.alt}
                              loading="lazy"
                              onError={(e) => handleImageError(e, image)}
                              style={{ minHeight: '220px', objectFit: 'cover' }}
                            />
                            <div className="slide-overlay">
                              <span>{image.displayName}</span>
                              {usingGoogleDrive && <small></small>}
                            </div>
                          </div>
                        ))}
                        
                        {/* Duplicate for seamless loop */}
                        {galleryImages.map((image, index) => (
                          <div key={`dup-${image.id || index}`} className="gallery-slide">
                            <img 
                              src={image.src} 
                              alt={`${image.alt} (2)`}
                              loading="lazy"
                              onError={(e) => handleImageError(e, image)}
                              style={{ minHeight: '220px', objectFit: 'cover' }}
                            />
                            <div className="slide-overlay">
                              <span>{image.displayName}</span>
                              {usingGoogleDrive && <small></small>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="gallery-controls">
                  
                  
                  <button 
                    onClick={toggleGalleryView}
                    className="btn-gallery"
                  >
                    {showAllGallery ? (
                      <>
                        <i className="bi bi-collection"></i> Show Slider View
                      </>
                    ) : (
                      <>
                        <i className="bi bi-grid-3x3"></i> View All Images
                      </>
                    )}
                  </button>
                  
                  <div className="gallery-instructions">
                    <small>
                      <i className="bi bi-info-circle"></i>
                      {window.innerWidth <= 768 ? 'Tap images' : 'Hover over images'} for details
                    </small>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Vision, Mission & Motto Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Vision, Mission & Motto</h2>
            <p>The guiding principles that define our approach to mental health care</p>
          </div>
          
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">👁️</div>
              <h3>Our Vision</h3>
              <p>
                To be Nagercoil's most trusted mental health sanctuary, creating a community where 
                emotional wellness is accessible to all. We envision a society where mental health care 
                is destigmatized and professional support is readily available for everyone.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>
                To provide compassionate, evidence-based counselling and homoeopathic treatment that 
                empowers individuals and families. We are committed to delivering personalized care 
                that addresses both psychological and physical well-being for holistic healing.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">❤️</div>
              <h3>Our Motto</h3>
              <p>
                <em>"Healing Hearts, Transforming Lives"</em><br/><br/>
                Through professional expertise and genuine compassion, we guide each individual 
                toward emotional resilience, personal growth and lasting positive change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Servite Counselling Centre?</h2>
            <p>What makes our approach to mental health care special and effective</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-number">01</div>
              <h3>Dual Expertise Approach</h3>
              <p>
                Unique combination of professional counselling psychology and holistic homoeopathic 
                medicine, offering comprehensive care that addresses both mental and physical health 
                aspects simultaneously.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">02</div>
              <h3>Personalized Treatment Plans</h3>
              <p>
                Every client receives a customized treatment plan tailored to their specific needs, 
                background and goals. No two therapy journeys are the same at our centre.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">03</div>
              <h3>Cultural & Linguistic Sensitivity</h3>
              <p>
                Deep understanding of Tamil Nadu's cultural context with services available in 
                multiple regional languages, ensuring comfort and effective communication for all clients.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">04</div>
              <h3>Evidence-Based Methods</h3>
              <p>
                All therapeutic approaches are grounded in scientifically validated techniques 
                combined with 25+ years of clinical experience, ensuring effective and reliable treatment outcomes.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">05</div>
              <h3>Confidential & Safe Environment</h3>
              <p>
                Strict adherence to professional ethics with complete confidentiality guaranteed. 
                Our centre provides a secure, non-judgmental space for healing and personal growth.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-number">06</div>
              <h3>Holistic Wellness Focus</h3>
              <p>
                We don't just treat symptoms - we work on overall life improvement including 
                relationships, career, personal development and physical health for complete well-being.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Guiding Principles</h2>
            <p>The foundation of our counselling and healing approach</p>
          </div>

          <div className="philosophy-grid">
            <div className="philosophy-card">
              <div className="card-header">
                <h3>Our Vision</h3>
              </div>
              <div className="card-content">
                <p>
                  To establish Servite Counselling Centre as Nagercoil's leading mental health sanctuary, 
                  providing professional counselling services where individuals find emotional healing, 
                  couples restore relationships and families achieve lasting psychological well-being.
                </p>
              </div>
            </div>

            <div className="philosophy-card">
              <div className="card-header">
                <h3>Our Mission</h3>
              </div>
              <div className="card-content">
                <p>
                  To deliver professional, confidential, and accessible mental health care through 
                  individual counselling, couples therapy and family counseling services in Nagercoil. 
                  We empower clients with practical coping strategies and therapeutic techniques.
                </p>
              </div>
            </div>

            <div className="philosophy-card">
              <div className="card-header">
                <h3>Our Promise</h3>
              </div>
              <div className="card-content">
                <p>
                  "Professional Compassion Heals: Restoring Hope, Transforming Lives Through Counselling."
                  Effective mental health treatment begins with professional expertise combined with 
                  genuine compassion, creating a therapeutic environment where healing flourishes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="commitment-section">
        <div className="container">
          <div className="section-header">
            <h2>Comprehensive Mental Health Services</h2>
            <p>Professional counselling and psychotherapy services designed to address diverse mental health challenges</p>
          </div>

          <div className="commitment-grid">
            <div className="commitment-item">
              <div className="item-number">01</div>
              <div className="item-content">
                <h4>Evidence-Based Depression & Anxiety Treatment</h4>
                <p>Professional counselling services using proven therapeutic methods to effectively manage depression, anxiety disorders and stress-related conditions.</p>
              </div>
            </div>

            <div className="commitment-item">
              <div className="item-number">02</div>
              <div className="item-content">
                <h4>Relationship & Marriage Counselling</h4>
                <p>Expert couples therapy and relationship counselling to improve communication, resolve conflicts and strengthen marital bonds.</p>
              </div>
            </div>

            <div className="commitment-item">
              <div className="item-number">03</div>
              <div className="item-content">
                <h4>Personal Development Coaching</h4>
                <p>Therapeutic support for personal growth, self-discovery and life transition management, helping clients achieve their full potential.</p>
              </div>
            </div>

            <div className="commitment-item">
              <div className="item-number">04</div>
              <div className="item-content">
                <h4>Family Counseling Services</h4>
                <p>Professional family therapy to improve dynamics, resolve intergenerational conflicts and create harmonious family relationships.</p>
              </div>
            </div>

            <div className="commitment-item">
              <div className="item-number">05</div>
              <div className="item-content">
                <h4>Child & Adolescent Psychology</h4>
                <p>Specialized counselling services for children and teenagers addressing behavioral issues, academic stress and emotional development.</p>
              </div>
            </div>

            <div className="commitment-item">
              <div className="item-number">06</div>
              <div className="item-content">
                <h4>Workplace Mental Wellness Programs</h4>
                <p>Corporate counselling and employee assistance programs designed to reduce workplace stress and promote organizational mental health.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Begin Your Mental Wellness Journey Today</h2>
            <p>
              Take the first step toward emotional well-being with professional counselling services. 
              Schedule your confidential therapy session today.
            </p>
            <div className="cta-buttons">
              <button 
                onClick={() => window.location.href = "/contact"}
                className="btn-primary"
              >
                Book Session
              </button>
              <button 
                onClick={() => window.location.href = 'tel:+919301098460'}
                className="btn-secondary"
              >
                Call Now
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;